import { z } from 'zod';
import type {
  InvestorWithRelations,
  InvestorReviewWithRelations,
  Investor,
  InvestorReview,
} from '../lib';

export type ListInvestorsResponse = {
  investors: Array<InvestorWithRelations>;
  offset: number;
  hasMore: boolean;
};

export type ListInvestorReviewsResponse = {
  reviews: Array<InvestorReviewWithRelations>;
};

export const listInvestorReviewsQuerySchema = z.object({
  userId: z.coerce.number().optional(),
});

export type ListInvestorReviewsQuery = z.infer<
  typeof listInvestorReviewsQuerySchema
>;

export const retrieveInvestorQuerySchema = z
  .object({
    id: z.coerce.number().optional(),
    slug: z.string().optional(),
  })
  .refine(
    (data) => {
      // Check that exactly one of `id` or `slug` is provided
      return (
        (data.id != null && data.slug == null) ||
        (data.id == null && data.slug != null)
      );
    },
    {
      message: "Either 'id' or 'slug' must be provided, but not both.",
    },
  );

export type RetrieveInvestorQuery = z.infer<typeof retrieveInvestorQuerySchema>;

export type RetrieveInvestorResponse = {
  investor: InvestorWithRelations;
};

export const listInvestorsQuerySchema = z.object({
  name: z.string().optional(),
  universities: z.array(z.string()).optional(),
  regions: z.array(z.string()).optional(),
  verticals: z.array(z.string()).optional(),
  minInvestment: z.coerce.number().optional(),
  maxInvestment: z.coerce.number().optional(),
  minAverageRating: z.coerce.number().optional(),
  tags: z.array(z.string()).optional(),
  limit: z.coerce.number().optional(),
  offset: z.coerce.number().optional(),
});

export type ListInvestorsQuery = z.infer<typeof listInvestorsQuerySchema>;

export const createInvestorReviewSchema = z.object({
  comment: z.string(),
  investmentProcessRating: z.number(),
  postInvestmentRating: z.number().optional(),
  investorId: z.number(),
});

export type CreateInvestorReviewBody = z.infer<
  typeof createInvestorReviewSchema
>;

export type CreateInvestorReviewResponse = {
  review: InvestorReview;
};

const optionalUrlSchema = z
  .string()
  .transform((val) => (val === '' ? undefined : val))
  .optional()
  .refine(
    (val) => val === undefined || z.string().url().safeParse(val).success,
    {
      message: 'Invalid URL',
    },
  );

export const createInvestorSchema = z
  .object({
    id: z.number().optional(),
    name: z.string().min(1, 'Investor name is required'),
    website: z.string().url('Website must be a valid URL'),
    regions: z.array(z.string()).optional(),
    verticals: z.array(z.string()).optional(),
    about: z
      .string()
      .max(1000, 'Description must be less than 1000 characters')
      .optional(),
    minInvestment: z.number().optional(),
    maxInvestment: z.number().optional(),
    twitterLink: optionalUrlSchema,
    linkedinLink: optionalUrlSchema,
  })
  .superRefine((data, ctx) => {
    // Check if both minInvestment and maxInvestment are provided, or neither
    if (
      (data.minInvestment === undefined) !==
      (data.maxInvestment === undefined)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Both minInvestment and maxInvestment must be provided, or neither',
        path: ['minInvestment', 'maxInvestment'],
      });
    }
    // Check if minInvestment is less than or equal to maxInvestment
    if (
      data.minInvestment !== undefined &&
      data.maxInvestment !== undefined &&
      data.minInvestment > data.maxInvestment
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'minInvestment must be less than or equal to maxInvestment',
        path: ['minInvestment', 'maxInvestment'],
      });
    }
  });

export type CreateInvestorBody = z.infer<typeof createInvestorSchema>;

export type CreateInvestorResponse = {
  investor: Investor;
};

export const editInvestorSchema = createInvestorSchema;

export type EditInvestorBody = z.infer<typeof editInvestorSchema>;

export type EditInvestorResponse = {
  investor: Investor;
};

export type RetrieveInvestorReviewResponse = {
  review: InvestorReviewWithRelations;
};

export const editInvestorReviewSchema = z.object({
  comment: z.string(),
  investmentProcessRating: z.number(),
  postInvestmentRating: z.number().nullable(),
});

export type EditInvestorReviewBody = z.infer<typeof editInvestorReviewSchema>;

export type EditInvestorReviewResponse = {
  review: InvestorReview;
};

export interface DeleteInvestorReviewResponse {
  success: boolean;
}
